// AIReportDetailScreen — full render of one AI report (docs/miniapp-m3-tz.md §3.8).
// Reads from the same ['ai-reports'] query cache the list populates (no extra
// endpoint in M-3). The digest body is Telegram HTML (<b>/<i>); we render bold/
// italic and linkify deal ids (#1986 → /deals/1986). On a cold deeplink the
// list query refetches and we find the report by id.
import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { fetchAiReports } from '../../api/director'
import { Badge, Button, Icon, ScreenSkeleton, t } from '../../design'

// Tokenise a Telegram-HTML line into styled runs + deal links. We split on the
// allowed tags and #id refs, toggling bold/italic as we walk; stray tags in
// text tokens are stripped.
function renderLine(line: string, onDeal: (id: number) => void, keyBase: string): ReactNode {
  const tokens = line.split(/(<\/?b>|<\/?i>|#\d+)/gi).filter((x) => x !== '')
  let bold = false
  let italic = false
  const out: ReactNode[] = []

  tokens.forEach((tok, i) => {
    const low = tok.toLowerCase()
    if (low === '<b>') { bold = true; return }
    if (low === '</b>') { bold = false; return }
    if (low === '<i>') { italic = true; return }
    if (low === '</i>') { italic = false; return }

    const dealMatch = /^#(\d+)$/.exec(tok)
    if (dealMatch) {
      const id = Number(dealMatch[1])
      out.push(
        <button
          key={`${keyBase}-${i}`}
          onClick={() => onDeal(id)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-500)', font: 'inherit', fontWeight: 600 }}
        >
          {tok}
        </button>,
      )
      return
    }

    const text = tok.replace(/<[^>]+>/g, '')
    if (!text) return
    out.push(
      <span key={`${keyBase}-${i}`} style={{ fontWeight: bold ? 700 : undefined, fontStyle: italic ? 'italic' : undefined }}>
        {text}
      </span>,
    )
  })

  return out
}

export function AIReportDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['ai-reports'], queryFn: fetchAiReports })

  if (isLoading) return <ScreenSkeleton kind="detail" />

  const report = data?.reports.find((r) => r.id === id)

  if (!report) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 14, padding: 24, textAlign: 'center' }}>
        <Icon.Sparkle size={30} style={{ color: 'var(--text-3)' }} />
        <div className="t-h2">{t('air.notFound')}</div>
        <Button variant="primary" icon={<Icon.ArrowLeft size={16} sw={2} />} onClick={() => navigate('/ai-reports')}>{t('air.back')}</Button>
      </div>
    )
  }

  const weekly = report.kind.includes('weekly')
  const lines = (report.body_md || '').split('\n')

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="row middle gap-2" style={{ marginBottom: 10 }}>
        <Badge tone={weekly ? 'ai' : 'neutral'} sm>
          <Icon.Sparkle size={10} sw={2.5} />{weekly ? t('air.weekly') : t('air.daily')}
        </Badge>
        {report.date && <span className="t-caption dim">{report.date}</span>}
      </div>

      <div className="card card-16">
        <div className="col" style={{ gap: 2 }}>
          {lines.map((line, i) =>
            line.trim() === '' ? (
              <div key={i} style={{ height: 8 }} />
            ) : (
              <div key={i} className="t-body" style={{ lineHeight: 1.5 }}>
                {renderLine(line, (dealId) => navigate(`/deals/${dealId}`), String(i))}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  )
}
